import {
  OverviewData,
  PortfolioData,
  TransactionsData,
  TopBuyersData,
  TopBuyersSummary,
  UserData,
  HeaderData,
  MenuItem
} from '../types';
import { authFetch } from '../utils/auth';

// Mock data for Overview page
export const mockOverviewData: OverviewData = {
  profitAndLoss: {
    revenue: 0,
    profit: 0,
    totalCost: 0,
    maxProfit: 0,
    potentialLoss: 0,
    minProfit: 0,
  },
  cost: {
    ethLockedInPool: 0,
    gas: 0,
    otherCost: 0,
  },
};

// Runtime header overrides populated from APIs
const runtimeHeaderOverrides: Partial<HeaderData> = {};

export function getHeaderData(): HeaderData {
  return {
    ...mockHeaderData,
    ...runtimeHeaderOverrides,
  };
}

export function setHeaderSubProject(subProject: string) {
  runtimeHeaderOverrides.subProject = subProject;
  // Notify app to update header when override changes
  try {
    window.dispatchEvent(new CustomEvent('header:subproject', { detail: subProject }));
  } catch {}
}

// Fetch Overview data from local API with mock fallback
export async function fetchOverviewData(): Promise<OverviewData> {
  try {
    const response = await authFetch('/pnl/overview');
    console.log("fetchOverviewData", response);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw = await response.json();
    const payload = raw?.data ?? raw; // support { data: {...} } wrapper

    // Attempt to map API shape to OverviewData; fallback to mock values when missing
    const overview: OverviewData = {
      profitAndLoss: {
        revenue: Number(payload?.profitAndLoss?.revenue ?? mockOverviewData.profitAndLoss.revenue),
        profit: Number(payload?.profitAndLoss?.profit ?? mockOverviewData.profitAndLoss.profit),
        totalCost: Number(payload?.profitAndLoss?.totalCost ?? mockOverviewData.profitAndLoss.totalCost),
        maxProfit: Number(payload?.profitAndLoss?.maxProfit ?? mockOverviewData.profitAndLoss.maxProfit),
        potentialLoss: Number(payload?.profitAndLoss?.potentialLoss ?? mockOverviewData.profitAndLoss.potentialLoss),
        minProfit: Number(payload?.profitAndLoss?.minProfit ?? mockOverviewData.profitAndLoss.minProfit),
      },
      cost: {
        ethLockedInPool: Number(payload?.cost?.ethLockedInPool ?? mockOverviewData.cost.ethLockedInPool),
        gas: Number(payload?.cost?.gas ?? mockOverviewData.cost.gas),
        otherCost: Number(payload?.cost?.otherCost ?? mockOverviewData.cost.otherCost),
      },
    };

    // Update header subProject if provided by API (either top-level or within profitAndLoss)
    const subProject = (
      payload?.subProject ??
      payload?.profitAndLoss?.subProject
    );
    if (typeof subProject === 'string' && subProject.trim()) {
      setHeaderSubProject(subProject);
    }

    return overview;
  } catch (err) {
    console.warn('Using mockOverviewData due to fetch error:', err);
    return mockOverviewData;
  }
}

// Mock data for Portfolio page
export const mockPortfolioData: PortfolioData = {
  summary: {
    totalETH: 39.330808,
    tokenInWallet: "947,968,298.67 (94.80%)",
    totalETHExceptPool: 14.332195,
  },
  detailETH: {
    general: {
      owner: 0.076163,
      deployer: 0.039675,
      botTrader: 3.086470,
    },
    operatorManual: {
      operatorManual: 0,
      trade: 10.951416,
    },
    operatorAuto: {
      operatorAuto: 0.178470,
      pool: 24.998613,
    },
  },
  detailtoken: {
    general: {
      owner: "0 (0%)",
      deployer: "0 (0%)",
      botTrader: "947,968,298.67 (94.80%)",
    },
    operatorManual: {
      operatorManual: "0 (0%)",
      trade: "0 (0%)",
    },
    operatorAuto: {
      operatorAuto: "0 (0%)",
      pool: "43,826,660.63 (4.38%)",
    },
  },
};

// Mock data for Transactions page
export const mockTransactionsData: TransactionsData = {
  summary: {
    bought: 1.939766,
    sold: 1.263123,
    profitAndLoss: 0.676643,
  },
  details: [
    {
      date: "16:03 14/10/2025",
      type: "Buy",
      usd: 24.76,
      eth: 0.006187,
      token: 51127,
      tokenPrice: "0.0(3)48432$",
      maker: "0x2c...5d2f",
      txnLink: "#",
    },
    {
      date: "16:03 14/10/2025",
      type: "Buy",
      usd: 4.95,
      eth: 0.001237,
      token: 10211,
      tokenPrice: "0.0(3)48500$",
      maker: "0xb1...403a",
      txnLink: "#",
    },
    {
      date: "16:03 14/10/2025",
      type: "Buy",
      usd: 11.89,
      eth: 0.00297,
      token: 24567,
      tokenPrice: "0.0(3)48381$",
      maker: "0xc6...e095",
      txnLink: "#",
    },
    {
      date: "16:03 14/10/2025",
      type: "Buy",
      usd: "<0.01",
      eth: 0.000001,
      token: 8,
      tokenPrice: "0.0(3)48249$",
      maker: "0xcb...8d0f",
      txnLink: "#",
    },
    {
      date: "16:03 14/10/2025",
      type: "Buy",
      usd: 15.23,
      eth: 0.003808,
      token: 31456,
      tokenPrice: "0.0(3)48415$",
      maker: "0xa2...7b8c",
      txnLink: "#",
    },
  ],
  pagination: {
    totalItems: 434,
    currentPage: 22,
    totalPages: 22,
  },
};

// Fetch Transactions summary from local API with auth and mock fallback
export async function fetchTransactionsSummary(): Promise<{ bought: number; sold: number; profitAndLoss: number; }> {
  try {
    const response = await authFetch('/pnl/summary');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw = await response.json();
    const payload = raw?.data ?? raw;
    return {
      bought: Number(payload?.bought ?? mockTransactionsData.summary.bought),
      sold: Number(payload?.sold ?? mockTransactionsData.summary.sold),
      profitAndLoss: Number(payload?.profitAndLoss ?? mockTransactionsData.summary.profitAndLoss),
    };
  } catch (err) {
    console.warn('Using mock transactions summary due to fetch error:', err);
    return mockTransactionsData.summary;
  }
}
const shorten = (addr: string): string => {
  if (!addr) return "";
  return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
};

// Fetch Transactions list with pagination; maps tokenAmount -> token
export async function fetchTransactions(page = 1, limit = 10): Promise<{ details: TransactionsData['details']; pagination: TransactionsData['pagination']; }> {
  try {
    const url = `/pnl/transactions?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
    const response = await authFetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw = await response.json();
    const payload = raw?.data ?? raw;

    const details = (payload?.details ?? []).map((item: any) => ({
      date: String(item?.date ?? ''),
      type: (String(item?.type ?? 'Buy') === 'Sell' ? 'Sell' : 'Buy') as 'Buy' | 'Sell',
      usd: typeof item?.usd === 'string' ? item.usd : Number(item?.usd ?? 0),
      eth: Number(item?.eth ?? 0),
      token: (item?.tokenAmount ?? item?.token ?? 0),
      tokenPrice: String(item?.tokenPrice ?? ''),
      maker: String(shorten(item?.maker) ?? ''),
      txnLink: String(item?.txnLink ?? ''),
    }));

    const pagination = {
      totalItems: Number(payload?.pagination?.totalItems ?? mockTransactionsData.pagination.totalItems),
      currentPage: Number(payload?.pagination?.currentPage ?? page),
      totalPages: Number(payload?.pagination?.totalPages ?? mockTransactionsData.pagination.totalPages),
    };

    return { details, pagination };
  } catch (err) {
    console.warn('Using mock transactions details due to fetch error:', err);
    return { details: mockTransactionsData.details, pagination: mockTransactionsData.pagination };
  }
}

// Mock data for Top Buyers page
export const mockTopBuyersData: TopBuyersData = {
  summary: {
    ethInUsersWallet: 2791.358495,
    tokenPrice: "0.0(6)57999",
    tokenInUsersWallet: "886,412.57 (0.09%)",
    potentialLoss: 0.503839,
    potentialLossInCustomPrice: 0,
  },
  details: [
    {
      address: "0x32...0de1",
      bought: "< 0.000001",
      sold: 0.009038,
      profitAndLoss: 0.009038,
      eth: 0.499589,
      weth: "< 0.000001",
      token: "-0",
      potentialLoss: "< 0.000001",
    },
    {
      address: "0xf2...1db2",
      bought: 0.086261,
      sold: 0.104857,
      profitAndLoss: 0.018597,
      eth: 0.105969,
      weth: "< 0.000001",
      token: "-0",
      potentialLoss: "< 0.000001",
    },
    {
      address: "0x5a...9c3e",
      bought: 0.023451,
      sold: 0.015623,
      profitAndLoss: "-0.007828",
      eth: 0.087234,
      weth: "< 0.000001",
      token: "-0",
      potentialLoss: "< 0.000001",
    },
    {
      address: "0x8d...2f7a",
      bought: 0.045123,
      sold: 0.032156,
      profitAndLoss: "-0.012967",
      eth: 0.156789,
      weth: "< 0.000001",
      token: "-0",
      potentialLoss: "< 0.000001",
    },
    {
      address: "0x1b...4e92",
      bought: 0.012345,
      sold: 0.018765,
      profitAndLoss: 0.00642,
      eth: 0.067890,
      weth: "< 0.000001",
      token: "-0",
      potentialLoss: "< 0.000001",
    },
  ],
  pagination: {
    totalItems: 213,
    currentPage: 1,
    totalPages: 11,
  },
};

// Fetch Top Buyers summary from local API with auth and mock fallback
export async function fetchTopBuyersSummary(): Promise<TopBuyersSummary> {
  try {
    const response = await authFetch('/pnl/top-buyers-summary');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw = await response.json();
    const payload = raw?.data ?? raw;
    const summary: TopBuyersSummary = {
      ethInUsersWallet: Number(payload?.ethInUsersWallet ?? mockTopBuyersData.summary.ethInUsersWallet),
      tokenPrice: String(payload?.tokenPrice ?? mockTopBuyersData.summary.tokenPrice),
      tokenInUsersWallet: String(payload?.tokenInUsersWallet ?? mockTopBuyersData.summary.tokenInUsersWallet),
      potentialLoss: Number(payload?.potentialLoss ?? mockTopBuyersData.summary.potentialLoss),
      potentialLossInCustomPrice: Number(payload?.potentialLossInCustomPrice ?? mockTopBuyersData.summary.potentialLossInCustomPrice),
    };
    return summary;
  } catch (err) {
    console.warn('Using mockTopBuyersData.summary due to fetch error:', err);
    return mockTopBuyersData.summary;
  }
}

// Fetch Top Buyers details from API with pagination
export async function fetchTopBuyers(page = 1, limit = 10): Promise<{ details: TopBuyersData['details']; pagination: TopBuyersData['pagination']; }> {
  try {
    const url = `/pnl/top-buyers?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
    console.log("fetchTopBuyers", url);
    const response = await authFetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw = await response.json();
    console.log({raw: JSON.stringify(raw)});
    const payload = raw?.data ?? raw;

    const details = (payload?.details ?? []).map((item: any) => ({
      address: String(shorten(item?.address ?? '')),
      bought: typeof item?.bought === 'string' ? item.bought : Number(item?.bought ?? 0),
      sold: typeof item?.sold === 'string' ? item.sold : Number(item?.sold ?? 0),
      profitAndLoss: typeof item?.profitAndLoss === 'string' ? item.profitAndLoss : Number(item?.profitAndLoss ?? 0),
      eth: typeof item?.eth === 'string' ? item.eth : Number(item?.eth ?? 0),
      weth: typeof item?.weth === 'string' ? item.weth : Number(item?.weth ?? 0),
      token: typeof item?.token === 'string' ? item.token : Number(item?.token ?? 0),
      potentialLoss: typeof item?.potentialLoss === 'string' ? item.potentialLoss : Number(item?.potentialLoss ?? 0),
    }));

    const pagination = {
      totalItems: Number(payload?.pagination?.totalItems ?? mockTopBuyersData.pagination.totalItems),
      currentPage: Number(payload?.pagination?.currentPage ?? page),
      totalPages: Number(payload?.pagination?.totalPages ?? mockTopBuyersData.pagination.totalPages),
    };

    return { details, pagination };
  } catch (err) {
    console.warn('Using mock top buyers details due to fetch error:', err);
    return { details: mockTopBuyersData.details, pagination: mockTopBuyersData.pagination };
  }
}

// Mock user data
export const mockUserData: UserData = {
  username: "alanwang",
  menuItems: [
    { label: "Overview", icon: "bar-chart", path: "/overview", active: true },
    { label: "Portfolio", icon: "folder", path: "/portfolio" },
    { label: "Transactions", icon: "arrows", path: "/transactions" },
    { label: "Top Buyers", icon: "trophy", path: "/buyers" },
  ],
  accountActions: [
    { label: "Change Password", icon: "lock", path: "/change-password" },
    { label: "Logout", icon: "logout", path: "/logout", color: "red" },
  ],
};

// Mock header data
export const mockHeaderData: HeaderData = {
  projectName: "Project Management",
  subProject: "Test",
  baseCurrency: "Base",
  displayCurrency: "ETH",
  monitorPlanningTabs: [
    { label: "Monitor", active: true },
  ],
};

// Helper function to get menu items for current page
export const getMenuItemsForPage = (currentPath: string): MenuItem[] => {
  return mockUserData.menuItems
    // Temporarily hide Portfolio from navigation
    .filter(item => item.label !== 'Portfolio')
    .map(item => ({
      ...item,
      active: item.path === currentPath
    }));
};
